import React, { Component } from "react";
import Select from "react-select";
import {
  Grid,
  Row,
  Col,
  FormControl,
  FormGroup,
  ControlLabel,
  Table,
} from "react-bootstrap";
import NotificationSystem from "react-notification-system";
import Card from "components/Card/Card.jsx";
import Button from "components/CustomButton/CustomButton.jsx";
import { style } from "../../../variables/Variables";
import { getApiErrorMessage } from "utils/getApiErrorMessage";
import {
  uploadPackageLeadImportV2,
  previewPackageLeadImportV2,
  applyPackageLeadImportV2,
  getPackageLeadImportV2Status,
  getPackageLeadImportV2FailedRows,
  retryPackageLeadImportV2Wallets,
  getPackagesWithoutPagination,
} from "api/packageLeadImport/packageLeadImport";

const fields = {
  mobile_no: "Mobile Number",
  name: "Name",
  email: "Email",
  policy_number: "Policy Number",
  gender: "Gender",
  date_of_birth: "Date of Birth",
  claim_value: "Claim Wallet Value",
};

class PackageLeadImportV2 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      file: null,
      packages: [],
      packageId: "",
      packageEndDate: "",
      importId: "",
      headers: [],
      mapping: {
        mobile_no: "",
        name: "",
        email: "",
        policy_number: "",
        gender: "",
        date_of_birth: "",
        claim_value: "",
      },
      uploadProgress: 0,
      status: null,
      failedRows: [],
      uploading: false,
      previewing: false,
      applying: false,
    };
  }

  componentDidMount() {
    this.setState({ _notificationSystem: this.refs.notificationSystem });
    const user = JSON.parse(localStorage.getItem("user"));

    if (!user || (user.role != 1 && user.role != 2)) {
      this.props.history.push("/dashboard");
      return;
    }

    getPackagesWithoutPagination(user.token)
      .then((res) => {
        this.setState({ packages: res.data || [] });
      })
      .catch((error) => {
        this.notify("error", getApiErrorMessage(error) || "Unable to load packages.");
      });
  }

  componentWillUnmount() {
    if (this.statusTimer) {
      clearTimeout(this.statusTimer);
    }
  }

  token() {
    const user = JSON.parse(localStorage.getItem("user"));
    return user ? user.token : "";
  }

  notify(level, message) {
    if (!this.state._notificationSystem) {
      return;
    }

    this.state._notificationSystem.addNotification({
      title: (
        <span
          data-notify="icon"
          className={level === "success" ? "pe-7s-check" : "pe-7s-close-circle"}
        />
      ),
      message: <div>{message}</div>,
      level,
      position: "bl",
      autoDismiss: 8,
    });
  }

  packageOptions() {
    return this.state.packages.map((pkg) => ({
      value: String(pkg.id),
      label: pkg.title,
    }));
  }

  selectedPackageOption() {
    const packageId = String(this.state.packageId || "");
    return this.packageOptions().find((option) => option.value === packageId) || null;
  }

  handlePackageChange = (selected) => {
    this.setState({
      packageId: selected ? selected.value : "",
      importId: "",
      headers: [],
      status: null,
      failedRows: [],
    });
  };

  handleInputChange = (event) => {
    this.setState({ [event.target.name]: event.target.value });
  };

  handleFileChange = (event) => {
    this.setState({
      file: event.target.files[0],
      importId: "",
      headers: [],
      status: null,
      failedRows: [],
      uploadProgress: 0,
    });
  };

  handleMappingChange = (field, value) => {
    this.setState((prevState) => ({
      mapping: {
        ...prevState.mapping,
        [field]: value,
      },
    }));
  };

  upload = () => {
    if (!this.state.file) {
      this.notify("error", "Please choose an Excel file.");
      return;
    }
    if (!this.state.packageId) {
      this.notify("error", "Please select a package.");
      return;
    }

    this.setState({ uploading: true, uploadProgress: 0, status: null, failedRows: [] });
    uploadPackageLeadImportV2(
      this.token(),
      this.state.file,
      this.state.packageId,
      this.state.packageEndDate,
      (progress) => this.setState({ uploadProgress: progress }),
    )
      .then((res) => {
        const run = res.data || {};
        this.setState({
          importId: run.import_id || "",
          headers: run.headers || [],
          mapping: {
            ...this.state.mapping,
            ...(run.suggested_mapping || {}),
          },
          status: run,
        });
        this.notify("success", "Upload completed.");
      })
      .catch((error) => {
        this.notify("error", getApiErrorMessage(error) || "Upload failed.");
      })
      .then(() => this.setState({ uploading: false }));
  };

  preview = () => {
    if (!this.state.importId) {
      this.notify("error", "Please upload a file first.");
      return;
    }

    this.setState({ previewing: true, failedRows: [] });
    previewPackageLeadImportV2(this.token(), this.state.importId, this.state.mapping)
      .then((res) => {
        this.setState({
          status: res.data || { status: "preview_queued" },
        });
        this.notify("success", "Preview started.");
        this.pollStatus();
      })
      .catch((error) => {
        this.notify("error", getApiErrorMessage(error) || "Preview failed.");
      })
      .then(() => this.setState({ previewing: false }));
  };

  apply = () => {
    if (!this.state.importId) {
      this.notify("error", "Please run preview first.");
      return;
    }

    this.setState({ applying: true });
    applyPackageLeadImportV2(this.token(), this.state.importId)
      .then((res) => {
        this.setState({ status: res.data || { status: "apply_queued" } });
        this.notify("success", "Apply started.");
        this.pollStatus();
      })
      .catch((error) => {
        this.notify("error", getApiErrorMessage(error) || "Apply failed.");
      })
      .then(() => this.setState({ applying: false }));
  };

  retryWallets = () => {
    retryPackageLeadImportV2Wallets(this.token(), this.state.importId)
      .then(() => {
        this.notify("success", "Wallet retry started.");
        this.pollStatus();
      })
      .catch((error) => {
        this.notify("error", getApiErrorMessage(error) || "Wallet retry failed.");
      });
  };

  canApply() {
    const status = this.state.status && this.state.status.status;
    return status === "previewed" || status === "apply_queued";
  }

  applyButtonLabel() {
    const status = this.state.status && this.state.status.status;
    if (this.state.applying) {
      return "Applying";
    }
    if (status === "apply_queued") {
      return "Requeue Apply";
    }

    return "Apply";
  }

  loadFailedRows = () => {
    getPackageLeadImportV2FailedRows(this.token(), this.state.importId, 100)
      .then((res) => {
        this.setState({ failedRows: res.data || [] });
      })
      .catch((error) => {
        this.notify("error", getApiErrorMessage(error) || "Unable to load failed rows.");
      });
  };

  pollStatus = () => {
    if (!this.state.importId) {
      return;
    }

    getPackageLeadImportV2Status(this.token(), this.state.importId)
      .then((res) => {
        const data = res.data || {};
        this.setState({ status: data });

        if (["preview_queued", "preview_processing", "apply_queued", "apply_processing"].indexOf(data.status) !== -1) {
          this.statusTimer = setTimeout(this.pollStatus, 5000);
          return;
        }

        if (["previewed", "completed", "failed"].indexOf(data.status) !== -1) {
          this.loadFailedRows();
        }
      })
      .catch(() => {
        this.statusTimer = setTimeout(this.pollStatus, 10000);
      });
  };

  renderHeaderOptions() {
    return (
      <React.Fragment>
        <option value="">Not mapped</option>
        {this.state.headers.map((header) => (
          <option key={header.index} value={header.index}>
            {header.name || `Column ${header.index + 1}`}
          </option>
        ))}
      </React.Fragment>
    );
  }

  renderCounters(title, counters) {
    if (!counters) {
      return null;
    }

    return (
      <div style={{ marginTop: 15 }}>
        <h5>{title}</h5>
        <Table striped responsive>
          <tbody>
            {Object.keys(counters).map((key) => (
              <tr key={key}>
                <td>{key.replace(/_/g, " ")}</td>
                <td>{counters[key] || 0}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }

  renderStatus() {
    const status = this.state.status;
    if (!status) {
      return null;
    }

    return (
      <div>
        <h5>Import Status</h5>
        <Table striped responsive>
          <tbody>
            <tr>
              <td>Status</td>
              <td>{status.status || "-"}</td>
            </tr>
            <tr>
              <td>Import ID</td>
              <td>{status.import_id || this.state.importId || "-"}</td>
            </tr>
            <tr>
              <td>Error</td>
              <td>{status.error_message || "-"}</td>
            </tr>
          </tbody>
        </Table>
        {this.renderCounters("Rows", status.rows)}
        {this.renderCounters("Preview", status.preview_summary)}
        {this.renderCounters("Apply", status.apply_summary)}
        {this.renderCounters("Wallets", status.wallets)}
      </div>
    );
  }

  renderFailedRows() {
    if (!this.state.failedRows.length) {
      return null;
    }

    return (
      <div style={{ marginTop: 20 }}>
        <h5>Failed Rows</h5>
        <Table striped responsive>
          <thead>
            <tr>
              <th>Row</th>
              <th>Status</th>
              <th>Lead</th>
              <th>Mobile</th>
              <th>Policy</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            {this.state.failedRows.map((row) => (
              <tr key={row.id}>
                <td>{row.row_number || "-"}</td>
                <td>{row.status}</td>
                <td>{row.lead_id || "-"}</td>
                <td>{row.normalized && row.normalized.mobile_no}</td>
                <td>{row.normalized && row.normalized.policy_number}</td>
                <td>{row.error_message || row.error_code || "-"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }

  render() {
    return (
      <div className="main-content">
        <Grid fluid>
          <NotificationSystem ref="notificationSystem" style={style} />
          <Row>
            <Col md={5}>
              <Card
                content={
                  <div>
                    <FormGroup>
                      <ControlLabel>Package</ControlLabel>
                      <Select
                        className="react-select primary"
                        classNamePrefix="react-select"
                        placeholder="Select package"
                        isClearable
                        value={this.selectedPackageOption()}
                        options={this.packageOptions()}
                        onChange={this.handlePackageChange}
                      />
                    </FormGroup>
                    <FormGroup>
                      <ControlLabel>Package End Date</ControlLabel>
                      <FormControl
                        type="date"
                        name="packageEndDate"
                        value={this.state.packageEndDate}
                        onChange={this.handleInputChange}
                      />
                    </FormGroup>
                    <FormGroup>
                      <ControlLabel>Excel File</ControlLabel>
                      <FormControl
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={this.handleFileChange}
                      />
                    </FormGroup>
                    <Button
                      class="btn-color"
                      fill
                      disabled={this.state.uploading}
                      onClick={this.upload}
                    >
                      <i className="fa fa-upload" /> {this.state.uploading ? "Uploading" : "Upload"}
                    </Button>
                    {this.state.uploadProgress > 0 && (
                      <div style={{ marginTop: 10 }}>Upload progress: {this.state.uploadProgress}%</div>
                    )}

                    {this.state.headers.length > 0 && (
                      <div style={{ marginTop: 20 }}>
                        <h5>Header Mapping</h5>
                        {Object.keys(fields).map((field) => (
                          <FormGroup key={field}>
                            <ControlLabel>{fields[field]}</ControlLabel>
                            <FormControl
                              componentClass="select"
                              value={this.state.mapping[field] || ""}
                              onChange={(event) => this.handleMappingChange(field, event.target.value)}
                            >
                              {this.renderHeaderOptions()}
                            </FormControl>
                          </FormGroup>
                        ))}
                        <Button
                          class="btn-color"
                          fill
                          disabled={this.state.previewing}
                          onClick={this.preview}
                        >
                          <i className="fa fa-search" /> {this.state.previewing ? "Previewing" : "Preview"}
                        </Button>{" "}
                        <Button
                          bsStyle="danger"
                          fill
                          disabled={this.state.applying || !this.canApply()}
                          onClick={this.apply}
                        >
                          <i className="fa fa-check" /> {this.applyButtonLabel()}
                        </Button>
                      </div>
                    )}
                  </div>
                }
              />
            </Col>
            <Col md={7}>
              <Card
                content={
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <h4 style={{ marginTop: 0 }}>Package Lead Import V2</h4>
                      <div>
                        <Button bsStyle="default" disabled={!this.state.importId} onClick={this.pollStatus}>
                          <i className="fa fa-refresh" /> Refresh
                        </Button>{" "}
                        <Button bsStyle="default" disabled={!this.state.importId} onClick={this.retryWallets}>
                          <i className="fa fa-repeat" /> Retry Wallets
                        </Button>
                      </div>
                    </div>
                    {this.renderStatus()}
                    {this.renderFailedRows()}
                  </div>
                }
              />
            </Col>
          </Row>
        </Grid>
      </div>
    );
  }
}

export default PackageLeadImportV2;
